import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    FormControl,
    FormLabel,
    Select,
    FormHelperText,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Text,
    Spinner,
    useToast
} from "@chakra-ui/react";
import StarEle from "@/utils/StarElement";
import axiosInstance from "store/axios";
const BackendUrlEmail = process.env.NEXT_PUBLIC_BackendUrlEmail;


const CityStateSelector = ({ formData, setFormData, errors, setErrors }) => {
    const [showModal, setShowModal] = useState(false);
    const [showNoDataModal, setShowNoDataModal] = useState(false);
    const [selectedState, setSelectedState] = useState("");
    const [selectedCity, setSelectedCity] = useState("");
    const [apiCityData, setApiCityData] = useState(null);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dataAvailable, setDataAvailable] = useState(false);
    const toast = useToast();



    // Fetch city data from API
    useEffect(() => {
        fetchCityData();
    }, []);

    const fetchCityData = async () => {
        try {
            setLoading(true);

            // Use axiosInstance to fetch city data
            const response = await axiosInstance.get('city');
            const result = response.data;

            if (result.data && result?.data?.length > 0) {
                setApiCityData(result?.data);
                setDataAvailable(true);

                // Extract unique states
                const uniqueStates = [...new Set(result?.data?.map(item => item.state))]
                    .filter(Boolean)
                    .sort();

                setStates(uniqueStates);

                // If we have initial state from formData, set it and load its cities
                if (formData.state) {
                    setSelectedState(formData.state);

                    // Get cities for the selected state
                    const stateCities = result.data
                        .filter(item => item.state === formData.state)
                        .map(item => item.city)
                        .filter(Boolean)
                        .sort();

                    setCities(stateCities);

                    // If we have initial city from formData, set it
                    if (formData.city) {
                        setSelectedCity(formData.city);
                    }
                }
            } else {
                // If no data is returned, just log it but don't block the flow
                console.log("No city data available:", result);
                setDataAvailable(false);

                setStates([]);
                setCities([]);

                // If we have initial state and city from formData, keep them
                if (formData.state) {
                    setSelectedState(formData.state);
                }
                if (formData.city) {
                    setSelectedCity(formData.city);
                }

                // Show a non-blocking toast instead of a modal
                toast({
                    title: "Using local data",
                    description: "City data couldn't be loaded from server. Using your existing location.",
                    status: "info",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error fetching city data:", error);
            setDataAvailable(false);


            // Set empty states and cities arrays
            setStates([]);
            setCities([]);

            // Preserve existing state and city if available
            if (formData.state) {
                setSelectedState(formData.state);
            }
            if (formData.city) {
                setSelectedCity(formData.city);
            }

            // Show toast instead of modal
            toast({
                title: "Using local data",
                description: "Unable to load location data. Using your existing location.",
                status: "info",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Update cities when state changes
    useEffect(() => {
        if (selectedState && apiCityData) {
            const stateCities = apiCityData
                .filter(item => item.state === selectedState)
                .map(item => item.city)
                .filter(Boolean);

            setCities(stateCities);
        } else {
            setCities([]);
        }
    }, [selectedState, apiCityData]);

    useEffect(() => {

        if (formData.state) {
            setSelectedState(formData.state);

            // If we have apiCityData, update the cities list for this state
            if (apiCityData) {
                const stateCities = apiCityData
                    .filter(item => item.state === formData.state)
                    .map(item => item.city)
                    .filter(Boolean);

                setCities(stateCities);
            }
        }

        if (formData.city) {
            setSelectedCity(formData.city);
        }
    }, [formData, apiCityData]);

    const handleStateChange = (e) => {
        const state = e.target.value;
        setSelectedState(state);
        setSelectedCity("");

        setFormData({
            ...formData,
            state: state,
            city: ""
        });

        // Clear errors when state is selected
        setErrors({
            ...errors,
            state: null
        });
    };

    const handleCityChange = (e) => {
        const city = e.target.value;
        setSelectedCity(city);

   
        const cityData = apiCityData?.find(item =>
            item.state === selectedState && item.city === city
        );

        const isAvailable = cityData?.available ?? false;

        if (isAvailable) {
            setFormData({
                ...formData,
                city: city
            });
            setErrors({
                ...errors,
                city: null
            });
        } else if (city) {
         
            setShowModal(true);
        }
    };

    const handleWaitlistSubmit = async () => {
        try {
            // Call the new interest API
            const response = await axios.post(`${BackendUrlEmail}/api/sendgrid/newIntrest`, {
                user_name: formData.first_name + " " + formData.last_name,
                email: formData.email,
                phone: formData.phone_number,
                city: selectedCity,
                state: selectedState
            });


            if (response.data) {
                toast({
                    title: "Success!",
                    description: "You've been added to our waitlist. We'll notify you when we expand to your area.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error submitting to waitlist:", error);
            toast({
                title: "Error",
                description: "Failed to add you to the waitlist. Please try again later.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setShowModal(false);
            setSelectedCity("");
            setFormData({
                ...formData,
                city: ""
            });
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCity("");
    };

    const handleCloseNoDataModal = () => {
        setShowNoDataModal(false);
    };

    return (
        <>
            <FormControl className="mb-4" isInvalid={errors.state}>
                <FormLabel className="form-label">State<StarEle /></FormLabel>
                <Select
                    placeholder={loading ? "Loading states..." : "Select State"}
                    className="form-control"
                    name="state"
                    value={selectedState}
                    onChange={handleStateChange}
                    isDisabled={loading}
                >
                    {states.map((state) => (
                        <option key={state} value={state}>
                            {state}
                        </option>
                    ))}
                    {selectedState && states.length === 0 && (
                        <option key={selectedState} value={selectedState}>
                            {selectedState}
                        </option>
                    )}
                </Select>
                {loading && <Spinner size="sm" ml={2} mt={2} />}
                {errors.state && (
                    <FormHelperText style={{ color: "red" }}>
                        {errors.state}
                    </FormHelperText>
                )}
            </FormControl>

            <FormControl className="mb-4" isInvalid={errors.city}>
                <FormLabel className="form-label">City<StarEle /></FormLabel>
                <Select
                    placeholder={!selectedState ? "Select State first" : loading ? "Loading cities..." : "Select City"}
                    className="form-control"
                    name="city"
                    value={selectedCity}
                    onChange={handleCityChange}
                    isDisabled={!selectedState || loading}
                >
                    {selectedState && cities.map((city) => (
                        <option key={city} value={city}>
                            {city}
                        </option>
                    ))}
                    {selectedState && selectedCity && cities.length === 0 && (
                        <option key={selectedCity} value={selectedCity}>
                            {selectedCity}
                        </option>
                    )}
                </Select>
                {loading && selectedState && <Spinner size="sm" ml={2} mt={2} />}
                {errors.city && (
                    <FormHelperText style={{ color: "red" }}>
                        {errors.city}
                    </FormHelperText>
                )}
            </FormControl>

            {/* Waitlist Modal */}
            <Modal isOpen={showModal} onClose={handleCloseModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Join Waitlist</ModalHeader>
                    <ModalBody>
                        <Text>
                            Currently, SkilSoar is not available in {selectedCity}, {selectedState}.
                            Would you like to join our waitlist? We'll notify you when
                            we expand to your area.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleWaitlistSubmit}>
                            Join Waitlist
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* No Data Modal - Only show if explicitly enabled */}
            {showNoDataModal && (
                <Modal isOpen={showNoDataModal} onClose={handleCloseNoDataModal}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Location Data Unavailable</ModalHeader>
                        <ModalBody>
                            <Text>
                                We're currently unable to load location data. You can try again later or continue with your existing location.
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" onClick={handleCloseNoDataModal}>
                                Continue
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </>
    );
};

export default CityStateSelector;